/**
 * Modal de alta de usuario — Seguridad / Usuarios (admin-only).
 *
 * Los usuarios se dan de alta SIN password — el acceso es por Google SSO.
 * El backend genera un hash placeholder para satisfacer la columna NOT NULL,
 * pero nadie puede loguear con él (no se conoce el plaintext).
 */

import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from '@/lib/notify';
// El username NO se pide acá. El backend lo deriva del email — el login
// por Google matchea por email contra la columna `email`, no por username.
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  useCrearUsuarioMutation,
  useRolesAdmin,
  useUbicaciones,
} from '@/api/queries';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NuevoUsuarioDialog({ open, onOpenChange }: Props) {
  const rolesQ = useRolesAdmin();
  const ubicacionesQ = useUbicaciones();
  const mutation = useCrearUsuarioMutation();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [roleId, setRoleId] = useState<number | null>(null);
  const [glnIds, setGlnIds] = useState<number[]>([]);
  const [defaultGlnId, setDefaultGlnId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Si el default deja de estar en la lista de glnIds, limpiarlo.
  useEffect(() => {
    if (defaultGlnId != null && !glnIds.includes(defaultGlnId)) {
      setDefaultGlnId(null);
    }
  }, [glnIds, defaultGlnId]);

  // Reset al abrir.
  useEffect(() => {
    if (open) {
      setEmail('');
      setName('');
      setRoleId(null);
      setGlnIds([]);
      setDefaultGlnId(null);
      setError(null);
    }
  }, [open]);

  const roles = rolesQ.data?.roles ?? [];
  const ubicaciones = ubicacionesQ.data ?? [];
  const cargando = rolesQ.isLoading || ubicacionesQ.isLoading;

  const emailValido = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email), [email]);
  const puedeGuardar =
    emailValido &&
    name.trim().length > 0 &&
    roleId != null &&
    glnIds.length > 0 &&
    !mutation.isPending;

  const toggleGln = (id: number) => {
    setGlnIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleGuardar = async () => {
    setError(null);
    if (!puedeGuardar || roleId == null) return;
    try {
      await mutation.mutateAsync({
        email: email.trim(),
        name: name.trim(),
        roleId,
        glnIds,
        defaultGlnId: defaultGlnId ?? (glnIds.length === 1 ? glnIds[0] : null),
      });
      toast.success(`Usuario ${email.trim()} creado`);
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Nuevo usuario</DialogTitle>
          <DialogDescription>
            El acceso es solo por Google SSO — no se setea password. El usuario
            podrá entrar con el email que ingreses acá.
          </DialogDescription>
        </DialogHeader>

        {cargando ? (
          <div className="flex items-center justify-center py-8 text-gray-600">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            <span>Cargando catálogos...</span>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="nu-email">
                Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="nu-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nombre@dominio.com"
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="nu-name">
                Nombre completo <span className="text-red-500">*</span>
              </Label>
              <Input
                id="nu-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Apellido, Nombre"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="nu-role">
                Rol <span className="text-red-500">*</span>
              </Label>
              <select
                id="nu-role"
                value={roleId ?? ''}
                onChange={(e) =>
                  setRoleId(e.target.value === '' ? null : Number(e.target.value))
                }
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">— Elegí un rol —</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                    {r.description ? ` · ${r.description}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label>
                GLN(s) operables <span className="text-red-500">*</span>
                <span className="ml-1 text-[11px] font-normal text-gray-500">
                  (marcá uno como default)
                </span>
              </Label>
              <div className="max-h-48 overflow-y-auto rounded border border-gray-200">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-[11px] uppercase text-gray-600">
                    <tr>
                      <th className="w-10 px-2 py-1.5 text-center">Op.</th>
                      <th className="w-12 px-2 py-1.5 text-center">Def.</th>
                      <th className="px-2 py-1.5 text-left">GLN</th>
                      <th className="px-2 py-1.5 text-left">Nombre</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ubicaciones.map((u) => {
                      const id = Number(u.id);
                      const operable = glnIds.includes(id);
                      const isDefault = defaultGlnId === id;
                      return (
                        <tr key={u.id} className="border-t border-gray-100">
                          <td className="px-2 py-1.5 text-center">
                            <input
                              type="checkbox"
                              checked={operable}
                              onChange={() => toggleGln(id)}
                            />
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            <input
                              type="radio"
                              name="nu-default-gln"
                              checked={isDefault}
                              disabled={!operable}
                              onChange={() => setDefaultGlnId(id)}
                            />
                          </td>
                          <td className="px-2 py-1.5 font-mono text-[11px]">{u.gln}</td>
                          <td className="px-2 py-1.5">{u.nombre}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription className="text-xs">{error}</AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => {
              void handleGuardar();
            }}
            disabled={!puedeGuardar}
            className={cn(
              'bg-everex-naranja text-white hover:bg-everex-naranja-hover',
              mutation.isPending && 'opacity-70',
            )}
          >
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Crear usuario
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
